import React, { useEffect, useState } from "react";
import { Card, Descriptions, Tag, Image, Spin, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { HiPencil, HiArrowLeft, HiDownload } from "react-icons/hi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getAssociate, clearAssociate } from "../../../store/slice/associates";
import api from "../../../store/client";

function toAbsoluteUrl(url) {
  if (
    !url ||
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:")
  )
    return url;
  const base = (api.defaults.baseURL ?? "").replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
}

function AuthImage({ src, alt, width = 120, height = 120 }) {
  const [blobUrl, setBlobUrl] = React.useState(null);

  useEffect(() => {
    if (!src) return;
    let active = true;
    api.get(toAbsoluteUrl(src), { responseType: "blob" }).then((res) => {
      if (!active) return;
      setBlobUrl(URL.createObjectURL(res.data));
    });
    return () => {
      active = false;
    };
  }, [src]);

  if (!src) return <span className="text-gray-400">—</span>;
  if (!blobUrl) return <Spin size="small" />;
  return (
    <Image
      src={blobUrl}
      alt={alt}
      width={width}
      height={height}
      style={{ objectFit: "cover", borderRadius: 8 }}
    />
  );
}

const empty = <span className="text-gray-400">—</span>;
const val = (v) => v || empty;
const bool = (v) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag>;

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function section(doc, title, rows, startY) {
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(249, 115, 22); // orange-500
  doc.text(title, 14, startY);

  autoTable(doc, {
    startY: startY + 4,
    head: [],
    body: rows,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3 },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: [245, 245, 245], cellWidth: 65 },
      1: { cellWidth: "auto" },
    },
    margin: { left: 14, right: 14 },
  });

  return doc.lastAutoTable.finalY + 8;
}

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

async function fetchBase64(url) {
  if (!url) return null;
  try {
    const res = await api.get(toAbsoluteUrl(url), { responseType: "blob" });
    return await blobToBase64(res.data);
  } catch {
    return null;
  }
}

function needsNewPage(doc, y, needed = 20) {
  if (y + needed > 277) {
    doc.addPage();
    return 16;
  }
  return y;
}

async function exportPDF(a) {
  const user = a.user ?? {};
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  // Fetch document images before building PDF
  const [aadharB64, panB64] = await Promise.all([
    fetchBase64(a.aadharPath),
    fetchBase64(a.panPath),
  ]);

  // Title
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(40, 40, 40);
  doc.text("Associate Details", 14, 18);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(`ID: ${a.id}  |  Generated: ${formatDateTime(new Date().toISOString())}`, 14, 25);

  doc.setDrawColor(249, 115, 22);
  doc.setLineWidth(0.5);
  doc.line(14, 28, 196, 28);

  let y = 36;

  y = section(doc, "Personal Information", [
    ["Full Name",           user.fullName        || "—"],
    ["Email",               user.email           || "—"],
    ["Phone Number",        user.phoneNumber      || "—"],
    ["WhatsApp Number",     a.whatsappNumber      || "—"],
    ["Date of Birth",       formatDate(a.dob)     || "—"],
    ["Gender",              a.gender              || "—"],
    ["Status",              a.isActive ? "Active" : "Inactive"],
    ["Has Smartphone",      a.hasSmartphone      ? "Yes" : "No"],
    ["Experienced",         a.experienced        ? "Yes" : "No"],
    ["Comfortable to Talk", a.comfortableToTalk  ? "Yes" : "No"],
  ], y);

  y = section(doc, "Location", [
    ["State",                     a.state                    || "—"],
    ["District",                  a.district                 || "—"],
    ["Post Office",               a.postOffice               || "—"],
    ["Police Station",            a.policeStation            || "—"],
    ["City / Town / Village",     a.cityTownVillage          || "—"],
    ["Primary Work Area Pincode", a.primaryWorkAreaPinCode   || "—"],
    ["Additional Work Areas",     (a.additionalWorkAreaPinCodes ?? []).join(", ") || "—"],
  ], y);

  y = section(doc, "Identity Documents", [
    ["Aadhar No.", a.aadharNo || "—"],
    ["PAN No.",    a.panNo    || "—"],
  ], y);

  // Aadhar & PAN images side by side
  const imgW = 80, imgH = 50;
  y = needsNewPage(doc, y, imgH + 10);
  if (aadharB64) {
    doc.addImage(aadharB64, "JPEG", 14, y, imgW, imgH);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Aadhar", 14, y + imgH + 4);
  }
  if (panB64) {
    doc.addImage(panB64, "JPEG", 14 + imgW + 8, y, imgW, imgH);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("PAN", 14 + imgW + 8, y + imgH + 4);
  }
  if (aadharB64 || panB64) y += imgH + 12;

  y = section(doc, "Payment Details", [
    ["Preferred Method", a.preferredPaymentMethod || "—"],
    ["UPI",              a.upi                    || "—"],
    ["Account Number",   a.accountNumber          || "—"],
    ["IFSC",             a.ifsc                   || "—"],
  ], y);

  // Declaration
  y = needsNewPage(doc, y, 40);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(249, 115, 22);
  doc.text("Declaration", 14, y);
  y += 6;

  const declaration =
    "I agreed to follow all platform guidelines and understand that this is " +
    "commission-based work with no fixed salary — commission will be provided " +
    "per successful registration.";
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(40, 40, 40);
  const lines = doc.splitTextToSize(declaration, 182);
  doc.text(lines, 14, y);
  y += lines.length * 5 + 12;

  // Signature
  y = needsNewPage(doc, y, 30);
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.4);
  doc.line(14, y + 18, 90, y + 18);
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text("Associate Signature", 14, y + 23);
  doc.text(`Name: ${user.fullName || ""}`, 14, y + 28);
  doc.text(`Date: ${formatDate(new Date().toISOString()) || ""}`, 14, y + 33);

  doc.save(`associate_${a.id}_${user.fullName?.replace(/\s+/g, "_") ?? "details"}.pdf`);
}

function AssociateDetails() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { associate, isLoading } = useSelector((state) => state.associates);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    dispatch(clearAssociate());
    dispatch(getAssociate(id));
  }, [id, dispatch]);

  if (isLoading || !associate || String(associate.id) !== id) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spin size="large" />
      </div>
    );
  }

  const a = associate;
  const user = a.user ?? {};
  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <Button
          icon={<HiArrowLeft className="inline mr-1" />}
          onClick={() => navigate("/associates")}
        >
          Back
        </Button>
        <div className="flex gap-2">
          <Button
            icon={<HiDownload className="inline mr-1" />}
            loading={exporting}
            onClick={async () => {
              setExporting(true);
              try { await exportPDF(a); } finally { setExporting(false); }
            }}
          >
            Export PDF
          </Button>
          <Button
            type="primary"
            icon={<HiPencil className="inline mr-1" />}
            onClick={() => navigate(`/associates/edit/${id}`)}
          >
            Edit
          </Button>
        </div>
      </div>

      {/* Personal Information */}
      <Card title="Personal Information">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Full Name">{val(user.fullName)}</Descriptions.Item>
          <Descriptions.Item label="Email">{val(user.email)}</Descriptions.Item>
          <Descriptions.Item label="Phone Number">{val(user.phoneNumber)}</Descriptions.Item>
          <Descriptions.Item label="WhatsApp Number">{val(a.whatsappNumber)}</Descriptions.Item>
          <Descriptions.Item label="Date of Birth">{val(formatDate(a.dob))}</Descriptions.Item>
          <Descriptions.Item label="Gender">{val(a.gender)}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={a.isActive ? "green" : "red"}>
              {a.isActive ? "Active" : "Inactive"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Has Smartphone">{bool(a.hasSmartphone)}</Descriptions.Item>
          <Descriptions.Item label="Experienced">{bool(a.experienced)}</Descriptions.Item>
          <Descriptions.Item label="Comfortable to Talk">{bool(a.comfortableToTalk)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Location */}
      <Card title="Location">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="State">{val(a.state)}</Descriptions.Item>
          <Descriptions.Item label="District">{val(a.district)}</Descriptions.Item>
          <Descriptions.Item label="Post Office">{val(a.postOffice)}</Descriptions.Item>
          <Descriptions.Item label="Police Station">{val(a.policeStation)}</Descriptions.Item>
          <Descriptions.Item label="City / Town / Village">{val(a.cityTownVillage)}</Descriptions.Item>
          <Descriptions.Item label="Primary Work Area Pincode">{val(a.primaryWorkAreaPinCode)}</Descriptions.Item>
          {a.additionalWorkAreaPinCodes?.length > 0 && (
            <Descriptions.Item label="Additional Work Area Pincodes" span={2}>
              <div className="flex flex-wrap gap-1">
                {a.additionalWorkAreaPinCodes.map((p) => (
                  <Tag key={p}>{p}</Tag>
                ))}
              </div>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Identity */}
      <Card title="Identity Documents">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Aadhar No.">{val(a.aadharNo)}</Descriptions.Item>
          <Descriptions.Item label="PAN No.">{val(a.panNo)}</Descriptions.Item>
        </Descriptions>
        <div className="flex flex-wrap gap-8 mt-4">
          {a.aadharPath && (
            <div className="flex flex-col items-center gap-1">
              <AuthImage src={a.aadharPath} alt="Aadhar" width={200} height={120} />
              <span className="text-xs text-gray-500">Aadhar</span>
            </div>
          )}
          {a.panPath && (
            <div className="flex flex-col items-center gap-1">
              <AuthImage src={a.panPath} alt="PAN" width={200} height={120} />
              <span className="text-xs text-gray-500">PAN</span>
            </div>
          )}
        </div>
      </Card>

      {/* Payment */}
      <Card title="Payment Details">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Preferred Method">{val(a.preferredPaymentMethod)}</Descriptions.Item>
          <Descriptions.Item label="UPI">{val(a.upi)}</Descriptions.Item>
          <Descriptions.Item label="Account Number">{val(a.accountNumber)}</Descriptions.Item>
          <Descriptions.Item label="IFSC">{val(a.ifsc)}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Audit */}
      <Card title="Audit">
        <Descriptions bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Created By">{val(a.createdBy)}</Descriptions.Item>
          <Descriptions.Item label="Created Date">{val(formatDateTime(a.createdDate))}</Descriptions.Item>
          <Descriptions.Item label="Updated By">{val(a.updatedBy)}</Descriptions.Item>
          <Descriptions.Item label="Updated Date">{val(formatDateTime(a.updatedDate))}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
}

export default AssociateDetails;
